/**
Copyright (C) 2019-2022, Infraclear Inc. All Rights Reserved.
*/

import _ from "lodash";
import dayjs from 'dayjs';
import { useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useState } from "react";
import SearchIcon from '@mui/icons-material/Search';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro";
import { Box, Button, IconButton, InputBase, Paper, Tooltip } from "@mui/material";
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import "./listUsers.scss";
import { MessageText } from "../../constants/messageText";
import { PermissionKey } from "../../constants/commonConstants";
import { DataTable } from "../../components/dataTable/dataTable";
import { SnackBarMessage } from "../../components/snackBar/snackBar";
import { RolePersmission } from "../../context/RolePermissionContext";
import { DownloadUserCSV } from "../../components/downloadCSV/downloadCSV";
import { UploadUsersPopUp } from "../../components/uploadUsers/uploadUsersPopup";
import instance from "../../utils/axiosInstance";
import { usersReport, payGUserReport, FetchTeams } from "../../constants/api-urlConstants";
import { apiBasePathUserDetail } from "../../constants/env-url";
import DownloadUserReport from "./downloadUser/downloadUser";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const ListUsers: React.FC<any> = (props: any) => {
  const navigate = useNavigate();
  const { permissionsObj } = React.useContext(RolePersmission);
  const [searchParam, setSearchParam] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState<any>([null, null]);
  const [openPopUp, setOpenPopUp] = React.useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    email: "",
    roles: [],
    companyName: "",
    status: [],
    referredUser: null,
    teamName: '',
    subscriptionStatuses: [] as string[],
  });
  const [msgConfig, setMsgConfig] = useState({
    type: '',
    message: '',
    snackFlag: false
  })
  const isFilterApplied = appliedFilters.email || appliedFilters.companyName || Boolean(appliedFilters.status.length) || Boolean(appliedFilters?.roles?.length) || Boolean(appliedFilters?.subscriptionStatuses?.length) || appliedFilters.teamName;
  const [teams, setTeams] = useState<any>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchTeamsList();
  }, [])
  const delayedQuery = useCallback(
    _.debounce((q) => searchUsers(q), 800),
    []
  );
  const searchUsers = (value: any) => {
    setSearchParam(value)
  }
  const onChange = (e: any) => {
    delayedQuery(e.target.value);
  };
  const handleDownloadReport = ({ url, payload }: { url: string, payload: any }) => {
    instance.post(url, payload).then(res => {
      if (res?.data?.data?.status === 'DOWNLOAD_SEARCH_TABLE_SUCCESS') {
        setSelectedDateRange([null, null]);
        setMsgConfig({
          type: 'success',
          message: MessageText.DOWNLOAD_SEARCH_TABLE_SUCCESS,
          snackFlag: true
        });
      } else {
        setMsgConfig({
          type: 'error',
          message: MessageText.DOWNLOAD_SEARCH_TABLE_ERROR,
          snackFlag: true
        });
      }
    }).catch(() => {
      setMsgConfig({
        type: 'error',
        message: MessageText.DOWNLOAD_SEARCH_TABLE_ERROR,
        snackFlag: true
      });
    });
  };

  const onClickDownloadSearchTable = () => {
    const payload = {
      ...appliedFilters,
      name: searchParam,
      startDate: selectedDateRange?.[0] ?? '',
      endDate: selectedDateRange?.[1] ?? ''
    };
    const url = `${apiBasePathUserDetail}${usersReport}`;
    handleDownloadReport({ url, payload });
  };

  const onClickDownloadPayGReport = (teamName: string) => {
    const payload = {
      email: appliedFilters?.email,
      teamName: teamName || "",
      startDate: selectedDateRange?.[0] ?? '',
      endDate: selectedDateRange?.[1] ?? ''
    };
    const url = `${apiBasePathUserDetail}${payGUserReport}`;
    handleDownloadReport({ url, payload });
  };

  const fetchTeamsList = () => {
    const TEAMS_API_BASE_URL = `${FetchTeams}`;
    instance.get(TEAMS_API_BASE_URL).then((res) => {
      const teams = res.data.data;
      setTeams(teams);
    });
  }

  return (
    <>
      <div className="list">
        <div className="containerWrapper">
          <div className="tableWrapper">
            <div className="listContainerWrapper">
              <div className="titleWrapper">
                <Box sx={{ display: 'flex', gap: '10px' }}>
                  <Paper
                    className="searchBox"
                  >
                    <InputBase
                      sx={{ ml: 1, flex: 1 }}
                      placeholder="Search users"
                      onChange={onChange}
                      type='search'
                    />
                    <IconButton aria-label="search">
                      <SearchIcon />
                    </IconButton>
                  </Paper>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateRangePicker
                      slots={{
                        field: SingleInputDateRangeField,
                      }}
                      slotProps={{
                        actionBar: { actions: [] },
                        field: {
                          onClick: () => setOpen(true),
                        }
                      }}
                      name="allowedRange"
                      className="custom-date-range-picker"
                      value={selectedDateRange}
                      onChange={(dateRange) => {
                        const fromDate = (dateRange?.[0] && dayjs(dateRange?.[0]).format('YYYY-MM-DD')) || '';
                        const toDate = (dateRange?.[1] && dayjs(dateRange?.[1]).format('YYYY-MM-DD')) || '';
                        if (fromDate || toDate) {
                          setSelectedDateRange(dateRange);
                        } else {
                          setSelectedDateRange([null, null]);
                        }
                      }}
                      maxDate={dayjs()}
                      open={open}
                      onOpen={() => setOpen(true)}
                      onClose={() => setOpen(false)}
                    />
                  </LocalizationProvider>
                  <DownloadUserReport
                    onClickDownloadPaygReport={onClickDownloadPayGReport}
                    onClickDownloadSearchTable={onClickDownloadSearchTable}
                    teams={teams}
                    isDisabled={!searchParam && (!dayjs(selectedDateRange?.[0]).isValid() || !dayjs(selectedDateRange?.[1]).isValid()) && !isFilterApplied}
                  />
                </Box>
                <div className="uploadUser" >
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    <DownloadUserCSV searchParamValue={searchParam} />
                    {
                      permissionsObj[PermissionKey.AUTH_UPLOAD_CSV_VIEW] &&
                      <IconButton sx={{ border: '1px solid', borderRadius: '4px' }} color="primary" aria-label="upload the cvs" onClick={() => {
                        setOpenPopUp(true)
                      }}>
                        <Tooltip title='Upload Create Account Template'>
                          <CloudUploadIcon />
                        </Tooltip>
                      </IconButton>
                    }
                    <Button className="primeryBtn" variant="contained" onClick={() => {
                      navigate("/users/usersEnrolment");
                    }}>
                      Create Accounts
                    </Button>
                  </Box>
                </div>
              </div>
              <DataTable searchParamValue={searchParam} setAppliedFilters={setAppliedFilters} />
            </div>
          </div>
        </div>
      </div>
      {
        openPopUp && <UploadUsersPopUp openModel={openPopUp}
          closeConfirmation={(data: any) => {
            if (data) {
              setMsgConfig({
                type: 'success',
                message: MessageText.VALIDFILE,
                snackFlag: true
              })
            }
            setOpenPopUp(false);
          }
          }
        />
      }
      <div>
        {
          msgConfig.snackFlag && <div>
            <SnackBarMessage snacBarValue={msgConfig}
            /></div>
        }
      </div>
    </>
  );
};

export default ListUsers;
